import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectCurrentUser } from "../../../store/slices/authSlice";
import {
	useGetUserDepartmentsQuery,
	useGetUserSubjectsQuery,
} from "../../../store/api/userApiSlice";
import {
	useGetFilteredAssessmentsQuery,
	useDeleteSubjectAssessmentMutation,
} from "../../../store/api/subjectAssessmentApiSlice";

// Constants
export const FIRST_SEMESTER_MONTHS = [
	"September",
	"October",
	"November",
	"December",
];
export const SECOND_SEMESTER_MONTHS = ["February", "March", "April", "May"];

// Helper to calculate current school year
const calculateSchoolYear = () => {
	const today = new Date();
	const year = today.getFullYear();
	const month = today.getMonth() + 1;

	return {
		start: month >= 9 ? `${year}-09-01` : `${year - 1}-09-01`,
		end: month >= 9 ? `${year + 1}-08-31` : `${year}-08-31`,
	};
};

// Helper to determine current semester based on date
const getCurrentSemester = () => {
	const currentDate = new Date();
	const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1

	// First semester: September (9) to December (12)
	// Second semester: January (1) to August (8)
	if (currentMonth >= 9 && currentMonth <= 12) {
		return "First Semester";
	} else {
		return "Second Semester";
	}
};

export const useAssessmentsData = () => {
	const { departmentId } = useParams();
	const user = useSelector(selectCurrentUser);

	// RTK Query hooks
	const { data: departments = [] } = useGetUserDepartmentsQuery();
	const { data: subjects = [] } = useGetUserSubjectsQuery();

	// State management - Initialize semester based on current date
	const [selectedSemester, setSelectedSemester] = useState(
		getCurrentSemester()
	);
	const [selectedSubject, setSelectedSubject] = useState("");
	const [selectedMonth, setSelectedMonth] = useState("");
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

	// Get school year
	const { start } = calculateSchoolYear();

	// RTK Query for assessments
	const {
		data: assessments = [],
		isLoading: assessmentsLoading,
		refetch: refetchAssessments,
	} = useGetFilteredAssessmentsQuery(
		{
			teacherId: user?.id,
			subjectId: selectedSubject,
			departmentId: departmentId,
			schoolYearStart: start,
		},
		{
			skip: !user?.id || !selectedSubject || !departmentId,
		}
	);

	// Mutations
	const [deleteAssessment] = useDeleteSubjectAssessmentMutation();

	// Get current department name
	const currentDepartment = departments.find(
		(dept) => dept.id === departmentId
	);

	// Get subjects for current department
	const departmentSubjects = useMemo(() => {
		if (!user?.assignedSubjects || !departmentId) return [];

		return user.assignedSubjects
			.filter((assignment) => assignment.departmentId.includes(departmentId))
			.map((assignment) => {
				const subject = subjects.find((s) => s.id === assignment.subjectId);
				return subject
					? { id: subject.id, name: subject.name }
					: { id: assignment.subjectId, name: assignment.subjectId };
			});
	}, [user?.assignedSubjects, departmentId, subjects]);

	// Handle window resize for mobile view
	useEffect(() => {
		const handleResize = () => {
			const newIsMobile = window.innerWidth <= 768;
			setIsMobile(newIsMobile);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Set default subject
	useEffect(() => {
		if (!selectedSubject && departmentSubjects.length > 0) {
			setSelectedSubject(departmentSubjects[0].id);
		}
	}, [departmentSubjects, selectedSubject]);

	// Set default selected month
	useEffect(() => {
		const monthsToDisplay =
			selectedSemester === "First Semester"
				? FIRST_SEMESTER_MONTHS
				: SECOND_SEMESTER_MONTHS;

		const currentMonth = new Date().toLocaleString("default", {
			month: "long",
		});

		if (monthsToDisplay.includes(currentMonth)) {
			setSelectedMonth(currentMonth);
		} else {
			setSelectedMonth(monthsToDisplay[0]);
		}
	}, [selectedSemester]);

	// Group assessments by month
	const groupedAssessments = useMemo(() => {
		return assessments.reduce((acc, assessment) => {
			const monthName = new Date(assessment.date).toLocaleString("default", {
				month: "long",
			});
			if (!acc[monthName]) acc[monthName] = [];
			acc[monthName].push(assessment);
			return acc;
		}, {});
	}, [assessments]);

	// Get months to display based on semester
	const monthsToDisplay =
		selectedSemester === "First Semester"
			? FIRST_SEMESTER_MONTHS
			: SECOND_SEMESTER_MONTHS;

	// Get current month's assessments
	const currentMonthAssessments = selectedMonth
		? groupedAssessments[selectedMonth] || []
		: [];

	// Calculate total points for current month
	const totalMonthPoints = currentMonthAssessments.reduce(
		(sum, assessment) => sum + Number(assessment.points),
		0
	);

	const handleDeleteAssessment = async (e, assessmentId) => {
		e.stopPropagation();
		if (window.confirm("Are you sure you want to delete this assessment?")) {
			try {
				await deleteAssessment(assessmentId).unwrap();
				refetchAssessments();
			} catch (error) {
				console.error("Failed to delete assessment:", error);
			}
		}
	};

	return {
		// Data
		user,
		departmentId,
		departments,
		subjects,
		currentDepartment,
		departmentSubjects,
		assessments,
		groupedAssessments,
		currentMonthAssessments,
		totalMonthPoints,
		monthsToDisplay,

		// State
		selectedSemester,
		setSelectedSemester,
		selectedSubject,
		setSelectedSubject,
		selectedMonth,
		setSelectedMonth,
		isMobile,

		// Loading states
		assessmentsLoading,

		// Actions
		refetchAssessments,
		handleDeleteAssessment,
	};
};
